/**
 * OnboardingContactPreferencesForm Component
 * SESSION_200: Onboarding Frontend Step 2 - Contact Preferences
 * SESSION_211: Added SMS default when phone exists + landline confirmation
 *
 * Form for business owners to configure their notification preferences
 * during the onboarding flow.
 *
 * Features:
 * - Toggle for email notifications (default: on)
 * - Toggle for SMS notifications (default: off, OR on if phone exists)
 * - Phone number input (required if SMS enabled)
 * - Phone auto-formatting as user types
 * - Landline confirmation modal for pre-filled phones
 */

import React, { useCallback, useState, useMemo } from 'react';
import {
  Stack,
  TextInput,
  Switch,
  Button,
  Group,
  Paper,
  Title,
  Text,
  Divider,
  Box,
  Alert
} from '@mantine/core';
import { IconAt, IconMail, IconMessageCircle, IconPhone, IconInfoCircle } from '@tabler/icons-react';
import type { OnboardingContactPreferencesFormProps, ContactPreferencesFormValues } from './OnboardingContactPreferencesForm.types';
import type { UpdateContactPreferencesRequest } from '../../../../domain/entities/Onboarding';
import {
  isValidPhoneNumber,
  formatPhoneAsYouType,
  normalizePhoneNumber,
  formatPhoneForDisplay
} from '../../../../utils/phoneValidation';
import { SmsConfirmationModal } from '../SmsConfirmationModal';
import { LandlineConfirmationModal } from '../LandlineConfirmationModal';

/**
 * Validates email format
 */
const isValidEmail = (email: string): boolean => {
  if (!email.trim()) return true; // Empty is valid (optional field)
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * OnboardingContactPreferencesForm - Form for setting notification preferences
 */
export const OnboardingContactPreferencesForm: React.FC<OnboardingContactPreferencesFormProps> = ({
  emailNotificationsEnabled: initialEmailEnabled,
  smsNotificationsEnabled: initialSmsEnabled,
  phoneNumber: initialPhoneNumber,
  businessPhone,
  userEmail,
  businessEmail,
  onSubmit,
  onBack,
  isLoading = false
}): React.ReactElement => {
  // SESSION_211: Determine available phone and source
  // User phone takes priority over business phone
  const availablePhone = useMemo((): string | null => {
    return initialPhoneNumber || businessPhone || null;
  }, [initialPhoneNumber, businessPhone]);

  const phoneSource = useMemo((): 'user' | 'business' | null => {
    if (initialPhoneNumber) return 'user';
    if (businessPhone) return 'business';
    return null;
  }, [initialPhoneNumber, businessPhone]);

  const hasPhoneOnFile = availablePhone !== null;

  // Form state - SESSION_211: Default SMS to ON if phone exists
  const [formValues, setFormValues] = useState<ContactPreferencesFormValues>(() => ({
    // Pre-fill email from user or business
    email: userEmail ?? businessEmail ?? '',
    emailNotificationsEnabled: initialEmailEnabled,
    // SESSION_211: Default SMS to ON if phone exists on file
    smsNotificationsEnabled: hasPhoneOnFile ? true : initialSmsEnabled,
    // Pre-fill phone number from user or business
    phoneNumber: availablePhone ? formatPhoneForDisplay(availablePhone) || '' : ''
  }));

  // Validation errors
  const [errors, setErrors] = useState<Partial<Record<keyof ContactPreferencesFormValues, string>>>({});

  // SESSION_201: SMS confirmation modal state
  const [showSmsConfirmation, setShowSmsConfirmation] = useState(false);

  // SESSION_211: Landline confirmation modal state
  const [showLandlineConfirmation, setShowLandlineConfirmation] = useState(false);
  const [pendingSubmitValues, setPendingSubmitValues] = useState<ContactPreferencesFormValues | null>(null);

  /**
   * Handle email notifications toggle
   */
  const handleEmailToggle = useCallback((checked: boolean): void => {
    setFormValues(prev => ({ ...prev, emailNotificationsEnabled: checked }));
  }, []);

  /**
   * Handle SMS notifications toggle
   */
  const handleSmsToggle = useCallback((checked: boolean): void => {
    setFormValues(prev => ({ ...prev, smsNotificationsEnabled: checked }));
    // Clear phone error when SMS is disabled
    if (!checked) {
      setErrors(prev => ({ ...prev, phoneNumber: undefined }));
    }
  }, []);

  /**
   * Handle phone number change with auto-formatting
   */
  const handlePhoneChange = useCallback((value: string): void => {
    const formatted = formatPhoneAsYouType(value);
    setFormValues(prev => ({ ...prev, phoneNumber: formatted }));
    // Clear error when user starts typing
    if (errors.phoneNumber) {
      setErrors(prev => ({ ...prev, phoneNumber: undefined }));
    }
  }, [errors]);

  /**
   * Handle email change
   */
  const handleEmailChange = useCallback((value: string): void => {
    setFormValues(prev => ({ ...prev, email: value }));
    // Clear error when user starts typing
    if (errors.email) {
      setErrors(prev => ({ ...prev, email: undefined }));
    }
  }, [errors]);

  /**
   * Validate form before submission
   */
  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<Record<keyof ContactPreferencesFormValues, string>> = {};

    // Validate email format if provided
    if (!isValidEmail(formValues.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone required if SMS enabled
    if (formValues.smsNotificationsEnabled && !formValues.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required when SMS notifications are enabled';
    }

    // Validate phone format if provided
    if (formValues.phoneNumber.trim() && !isValidPhoneNumber(formValues.phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid US phone number (e.g., (206) 555-1234)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formValues]);

  /**
   * Submit the form with the current form values
   */
  const submitForm = useCallback((values: ContactPreferencesFormValues): void => {
    // Normalize phone number to E.164 format for API
    const normalizedPhone = values.phoneNumber.trim()
      ? normalizePhoneNumber(values.phoneNumber)
      : null;

    const request: UpdateContactPreferencesRequest = {
      email: values.email.trim() || null,
      emailNotificationsEnabled: values.emailNotificationsEnabled,
      smsNotificationsEnabled: values.smsNotificationsEnabled,
      phoneNumber: normalizedPhone
    };

    onSubmit(request);
  }, [onSubmit]);

  /**
   * Handle form submission
   * SESSION_201: Show SMS confirmation modal if SMS is not enabled
   * SESSION_211: Show landline confirmation modal if SMS enabled with pre-filled phone
   */
  const handleSubmit = useCallback((e: React.FormEvent): void => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // SESSION_201: If SMS is not enabled, show confirmation modal
    if (!formValues.smsNotificationsEnabled) {
      setShowSmsConfirmation(true);
      return;
    }

    // SESSION_211: If SMS enabled and phone was pre-filled from file, show landline confirmation
    if (hasPhoneOnFile && phoneSource !== null) {
      setPendingSubmitValues(formValues);
      setShowLandlineConfirmation(true);
      return;
    }

    // SMS is enabled with user-entered phone, submit directly
    submitForm(formValues);
  }, [formValues, validateForm, submitForm, hasPhoneOnFile, phoneSource]);

  /**
   * SESSION_201: Handle enabling SMS from the confirmation modal
   */
  const handleEnableSmsFromModal = useCallback((phoneNumber: string): void => {
    // Normalize phone and submit with SMS enabled
    const updatedValues: ContactPreferencesFormValues = {
      ...formValues,
      smsNotificationsEnabled: true,
      phoneNumber
    };

    setShowSmsConfirmation(false);
    submitForm(updatedValues);
  }, [formValues, submitForm]);

  /**
   * SESSION_201: Handle continuing without SMS from the modal
   */
  const handleContinueWithoutSms = useCallback((): void => {
    setShowSmsConfirmation(false);
    submitForm(formValues);
  }, [formValues, submitForm]);

  /**
   * SESSION_211: Handle confirming mobile from landline confirmation modal
   */
  const handleConfirmMobile = useCallback((): void => {
    setShowLandlineConfirmation(false);
    if (pendingSubmitValues) {
      submitForm(pendingSubmitValues);
      setPendingSubmitValues(null);
    }
  }, [pendingSubmitValues, submitForm]);

  /**
   * SESSION_211: Handle "not mobile" from landline confirmation modal
   * Disables SMS and clears phone so user can enter a mobile number
   */
  const handleNotMobile = useCallback((): void => {
    setShowLandlineConfirmation(false);
    setPendingSubmitValues(null);
    // Disable SMS and clear phone since it's not mobile
    setFormValues(prev => ({
      ...prev,
      smsNotificationsEnabled: false,
      phoneNumber: ''
    }));
  }, []);

  /**
   * Handle back button click
   */
  const handleBack = useCallback((): void => {
    if (onBack) {
      onBack();
    }
  }, [onBack]);

  return (
    <>
      {/* SESSION_201: SMS Confirmation Modal */}
      <SmsConfirmationModal
        opened={showSmsConfirmation}
        onClose={() => setShowSmsConfirmation(false)}
        onEnableSms={handleEnableSmsFromModal}
        onContinueWithoutSms={handleContinueWithoutSms}
        isLoading={isLoading}
      />

      {/* SESSION_211: Landline Confirmation Modal */}
      <LandlineConfirmationModal
        opened={showLandlineConfirmation}
        onClose={() => {
          setShowLandlineConfirmation(false);
          setPendingSubmitValues(null);
        }}
        onConfirmMobile={handleConfirmMobile}
        onNotMobile={handleNotMobile}
        phoneNumber={availablePhone || ''}
        phoneSource={phoneSource || 'user'}
        isLoading={isLoading}
      />

      <Paper shadow="sm" p="xl" radius="md" withBorder>
        <form onSubmit={handleSubmit}>
        <Stack gap="lg">
          {/* Header */}
          <Box>
            <Title order={2} mb="xs">Contact Preferences</Title>
            <Text c="dimmed" size="sm">
              How should we contact you when we find mentions of your business?
            </Text>
          </Box>

          <Divider />

          {/* Info Alert */}
          <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
            We'll notify you when someone mentions your business online.
            You can change these settings anytime from your dashboard.
          </Alert>

          {/* Contact Email */}
          <Stack gap="md">
            <Text fw={600} size="sm" c="dimmed">Your Contact Email</Text>
            <TextInput
              label="Email Address"
              description="We'll send mention alerts and updates to this email"
              placeholder="you@example.com"
              leftSection={<IconAt size={16} />}
              value={formValues.email}
              onChange={(e) => handleEmailChange(e.currentTarget.value)}
              error={errors.email}
              disabled={isLoading}
            />
          </Stack>

          <Divider />

          {/* Notification Toggles */}
          <Stack gap="md">
            <Text fw={600} size="sm" c="dimmed">Notification Settings</Text>

            {/* Email Notifications */}
            <Group justify="space-between" wrap="nowrap">
              <Group gap="sm">
                <IconMail size={20} color="gray" />
                <Box>
                  <Text size="sm" fw={500}>Email Notifications</Text>
                  <Text size="xs" c="dimmed">
                    Receive email alerts for new mentions
                  </Text>
                </Box>
              </Group>
              <Switch
                checked={formValues.emailNotificationsEnabled}
                onChange={(e) => handleEmailToggle(e.currentTarget.checked)}
                disabled={isLoading}
                size="md"
              />
            </Group>

            {/* SMS Notifications */}
            <Group justify="space-between" wrap="nowrap">
              <Group gap="sm">
                <IconMessageCircle size={20} color="gray" />
                <Box>
                  <Text size="sm" fw={500}>SMS Notifications</Text>
                  <Text size="xs" c="dimmed">
                    Receive text message alerts for new mentions
                  </Text>
                </Box>
              </Group>
              <Switch
                checked={formValues.smsNotificationsEnabled}
                onChange={(e) => handleSmsToggle(e.currentTarget.checked)}
                disabled={isLoading}
                size="md"
              />
            </Group>

            {/* Phone Number (shown when SMS enabled) */}
            {formValues.smsNotificationsEnabled && (
              <Box mt="sm">
                <TextInput
                  label="Phone Number"
                  placeholder="(206) 555-1234"
                  description="US phone numbers only. Required for SMS notifications."
                  leftSection={<IconPhone size={16} />}
                  value={formValues.phoneNumber}
                  onChange={(e) => handlePhoneChange(e.currentTarget.value)}
                  error={errors.phoneNumber}
                  disabled={isLoading}
                  required
                />
              </Box>
            )}
          </Stack>

          <Divider />

          {/* Action Buttons */}
          <Group justify="space-between">
            {onBack && (
              <Button
                variant="subtle"
                color="gray"
                onClick={handleBack}
                disabled={isLoading}
              >
                Back
              </Button>
            )}
            <Button
              type="submit"
              loading={isLoading}
              ml={onBack ? 0 : 'auto'}
            >
              Complete Setup
            </Button>
          </Group>
        </Stack>
        </form>
      </Paper>
    </>
  );
};
